import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { AgendaHandle, NavigationKernel, StoredAgendaConfig } from '@/shared/navigation/core';
import { createTanStackRouterAdapter } from '@/shared/navigation/core';
import type { AgendaRegistration } from '@/shared/navigation';
import {
  useNavigationContext,
  useNavigationStore,
} from '@/shared/navigation/react/useNavigationContext';
import { NavigationShellFrame } from '@/shared/navigation/shell';
import { PhoenixDesktop, PhoenixTaskbar, useDocumentTitle } from '@/shared/windowing';
import { getRuntimeConfig } from '@/shared/runtimeConfig';
import { envColor, showEnvIndicator } from '@/shared/common/envColors';
import { ModeSwitcher, SchemeSwitcher } from '@/shared/ui/theme';
import {
  LanguageSwitcher,
  MainMenu,
  MenuSearch,
  toast,
  type MenuActionContext,
  type MenuSearchSpecial,
  type MenuSearchHandle,
} from '@/shared/ui';
import {
  useMagicMenu,
  toMenuViewModel,
  sendMagicMenuItemClick,
  sendMagicFormEdit,
  setPendingMenuOpen,
  isPhoenixOnTopMode,
  useMagicDesktopStore,
  MAGIC_TO_PHOENIX_ACTIONS,
  type MenuOpenMode,
} from '@/shared/legacyMagicBridge';
import { getBusinessCaseByNumber } from '@/api/generated/reservations/reservations';
import { useAuth, userHasPermission, withPermissionGate } from '@/shared/auth';
import {
  recordAgendaHistory,
  linkLayerToHistory,
  type AgendaHistoryItem,
} from '@/shared/lib/agendaHistory';
import { legacyFormAgenda } from '@/modules/legacy-form/legacyFormAgenda';
import { WindowOnboarding } from '@/modules/window-onboarding';
import { JobsRuntime, OpsIndicator } from '@/modules/jobs';
import { registerVenuaPlannerJobResultActions } from '@/modules/products';
import { DevelopmentDatabaseBadge } from './DevelopmentDatabaseBadge';
import { AgendaHistoryButton } from './AgendaHistoryButton';
import { DesktopHeaderControls } from './DesktopHeaderControls';
import {
  getNativeRootAgenda,
  findNativeAgendaById,
  getAllNativeAgendaRegistrations,
  installObjectCategoryNav,
} from '../router/nativeAgendaRegistry';
import { useAgendaHistoryRecorder } from './useAgendaHistoryRecorder';
import { getMainMenuItems } from './mainMenuDefinitions';
import type { StandardMenuItem } from './mainMenuItems';
import { mergeNativeIntoSections, type NativeMenuEntry } from './mergeNativeIntoSections';

// `AgendaRegistration` žije v `@/shared/navigation` (aby ho moduly mohly použít bez závislosti
// na app vrstvě); re-export drží zpětnou kompatibilitu pro `@/app/layout` konzumenty.
export type { AgendaRegistration };

export interface MenuNavigationShellPageProps {
  agendas: AgendaRegistration[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic agenda handle used only for openRoot identity.
  rootAgenda?: AgendaHandle<any, any>;
}

function registerAgendas(kernel: NavigationKernel, agendas: readonly AgendaRegistration[]): void {
  const registry = kernel.getRegistry();

  for (const { agenda, component, permission } of agendas) {
    if (!registry.has(agenda.id)) {
      kernel.registerAgenda(agenda.config as StoredAgendaConfig);
    }
    kernel.registerComponent(
      agenda.id,
      permission ? withPermissionGate(permission, component) : component
    );
  }
}

export function MenuNavigationShellPage({ agendas, rootAgenda }: MenuNavigationShellPageProps) {
  const { kernel } = useNavigationContext();
  const router = useRouter();
  const navigate = useNavigate();
  // Zapisuje do historie KAŽDÉ otevření native agendy (i zevnitř modulů) — přes event jádra.
  useAgendaHistoryRecorder();
  // Drží `document.title` = „{instance} · {titulek aktivního okna}" (reaguje na fokus i donačtení titulků).
  useDocumentTitle();
  // Aktivní položka menu = ta, jejíž root agenda je právě root výchozí plochy (cesty v URL už nejsou).
  const plochaRootAgendaId = useNavigationStore(
    (s) =>
      s.navigation.layers.find((l) => l.stackId === undefined && l.presentation === 'root')
        ?.agendaId ?? null
  );
  const { t } = useTranslation('common');
  const searchRef = useRef<MenuSearchHandle>(null);
  const magicVersion = useMagicDesktopStore((s) => s.userInfo?.magicVersion);
  // Z Magic verze („57test.0 (db: Venua)") vezmeme jen číslo verze, bez „(db: …)" přívěšku.
  const magicVersionShort = magicVersion?.replace(/\s*\(.*$/, '').trim();
  // Verzi Phoenixu bereme z runtime configu (per-deployment, stejný zdroj jako LoginGate patička),
  // NE z build-time verze v package.json (neudržovaný placeholder „1.0.0"). Prázdné = skryté.
  const { version: appVersion, environment } = getRuntimeConfig().app;
  const appLabel = appVersion ? `v${appVersion}` : '';
  const versionLabel = magicVersionShort
    ? appLabel
      ? `${appLabel} · v${magicVersionShort}`
      : `v${magicVersionShort}`
    : appLabel;
  // Indikátor prostředí (podbarvení celého menu + barva patičkového „ENV: …"). Barva dle environment
  // badge; ne-produkční prostředí (dev = modrá, env = zelená, …) podbarví menu, produkce/prázdné ne.
  const envTintColor = showEnvIndicator(environment) ? envColor(environment).bg : undefined;

  const baselineMenuItems = useMemo(() => getMainMenuItems(t), [t]);
  const { items: magicItems, isReady, loadError } = useMagicMenu();
  const phoenixOnTop = isPhoenixOnTopMode();
  const auth = useAuth();

  // Položky vázané na oprávnění (`permission`) zobraz jen tehdy, když ho přihlášený uživatel má
  // (config super-user vidí vše). Dokud není autentizace dokončená, gated položky skryj
  // (bezpečný default bez problikávání).
  const user = auth.status === 'authenticated' ? auth.user : null;
  const visibleMenuItems = useMemo(
    () =>
      baselineMenuItems.filter(
        (i) =>
          i.kind !== 'standard' ||
          i.permission === undefined ||
          userHasPermission(user, i.permission)
      ),
    [baselineMenuItems, user]
  );

  // Native položky s cílovou sekcí → merge do sekcí; bez sekce → samostatná tlačítka vlevo.
  const nativeEntries = useMemo<NativeMenuEntry[]>(
    () =>
      visibleMenuItems
        .filter((i): i is StandardMenuItem => i.kind === 'standard' && i.section !== undefined)
        .map((i) => ({
          id: i.path,
          title: i.label,
          section: i.section as string,
          sectionLabel: i.sectionLabel,
          group: i.group,
          groupLabel: i.groupLabel,
          key: i.key,
          replacesKey: i.replacesKey,
        })),
    [visibleMenuItems]
  );
  const leadingStandard = useMemo(
    () =>
      visibleMenuItems.filter(
        (i): i is StandardMenuItem => i.kind === 'standard' && i.section === undefined
      ),
    [visibleMenuItems]
  );

  const sections = useMemo(() => {
    const legacy = phoenixOnTop ? toMenuViewModel(magicItems) : [];
    return mergeNativeIntoSections(legacy, nativeEntries);
  }, [phoenixOnTop, magicItems, nativeEntries]);

  const loading = phoenixOnTop && !isReady && !loadError;
  const menuLoadError = phoenixOnTop && loadError;

  // Native open z menu/historie: otevři root agendu PŘÍMO přes openRoot (bez route navigace —
  // agendy žijí jen v search params, modulové cesty zrušeny). openRoot přes replaceWithRoot zachová
  // floating okna a funguje z libovolného stavu (i z prázdného kernelu). `key` je klíč native položky
  // (dřív cesta). Ne-agenda položka (jen `/design`) → klasická route navigace.
  const handleMenuNavigate = useCallback(
    (key: string) => {
      const root = getNativeRootAgenda(key);
      if (root) {
        void kernel.openRoot(root, {});
        return;
      }
      void navigate({ to: key } as Parameters<typeof navigate>[0]);
    },
    [kernel, navigate]
  );

  // Otevře native agendu jako OSAMOSTATNĚNÉ (floating) okno bez nahrazení plochy. Agenda je globálně
  // zaregistrovaná (viz registrační efekt níže), takže se otevře i bez návštěvy její route. Stejný
  // vzor jako legacy detached: openModal + fresh stackId → projekce ji zobrazí jako floating okno.
  const openNativeDetached = useCallback(
    (path: string) => {
      const root = getNativeRootAgenda(path);
      if (!root) {
        // Není to native modul s agendou (např. /design) → fallback na běžnou navigaci.
        handleMenuNavigate(path);
        return;
      }
      // Zápis do historie řeší useAgendaHistoryRecorder (naslouchá otevření vrstvy v jádru).
      void kernel.openModal(root, {}).then(() => {
        const newId = kernel.getState().activeTopLayerId;
        if (newId) {
          // Fresh stackId → projekce zobrazí okno jako floating (PhoenixDesktop naseeduje rect).
          kernel.setLayerStackId(newId, `s_${crypto.randomUUID()}`);
        }
      });
    },
    [kernel, handleMenuNavigate]
  );

  // Otevření položky menu. mode 'replace' = NAHRADIT komplet plochu (klik na položku),
  // mode 'detached' = otevřít v SAMOSTATNÉM (plovoucím) okně (čudlík vpravo).
  // Native replace → route navigace (přepíše plochu); native detached → openNativeDetached (floating);
  // legacy → záměr + dispatch do Magicu (bridge pak udělá openRoot/openModal + zapíše do historie).
  const handleOpen = useCallback(
    (ctx: MenuActionContext, mode: 'replace' | 'detached') => {
      const node = ctx.node;
      // Externí WWW odkaz → otevři v nové záložce PŘÍMO (synchronně v user-gestu, jinak by popup
      // blocker zablokoval). Žádný dispatch do Magicu, žádný záměr, do historie nepatří.
      if (node.url) {
        window.open(node.url, '_blank', 'noopener');
        return;
      }
      if (node.origin === 'native') {
        if (mode === 'detached') {
          openNativeDetached(node.id);
        } else {
          // Zápis do historie řeší useAgendaHistoryRecorder (otevření vrstvy v jádru).
          handleMenuNavigate(node.id);
        }
      } else {
        setPendingMenuOpen(mode, node.title);
        sendMagicMenuItemClick(node.id);
      }
    },
    [handleMenuNavigate, openNativeDetached]
  );

  // Našeptávač: otevření detailu obchodního případu dle čísla. Číslo → ID přes API; když existuje,
  // otevři legacy detail (ObjectCategory BusinessCase) přes `form/edit` (Magic probublá `form/open`,
  // bridge respektuje pending mode). Když ne, jen toast „nenalezen".
  const openBusinessCaseByNumber = useCallback(
    async (num: number, mode: MenuOpenMode) => {
      try {
        const res = await getBusinessCaseByNumber(num);
        if (res?.id != null) {
          setPendingMenuOpen(mode, t('businessCase.title', { number: num }));
          sendMagicFormEdit('BusinessCase', res.id);
        } else {
          toast({ severity: 'error', description: t('businessCase.notFound', { number: num }) });
        }
      } catch {
        toast({ severity: 'error', description: t('businessCase.notFound', { number: num }) });
      }
    },
    [t]
  );

  // Speciální výsledek našeptávače: čistě číselný dotaz → nabídka „Otevřít obchodní případ č. X" (první).
  const menuSearchSpecials = useCallback(
    (query: string): MenuSearchSpecial[] => {
      const trimmed = query.trim();
      if (!/^\d+$/.test(trimmed)) return [];
      const num = Number(trimmed);
      if (!Number.isSafeInteger(num)) return [];
      return [
        {
          id: `bc:${trimmed}`,
          title: t('businessCase.openByNumber', { number: trimmed }),
          icon: 'list',
          onActivate: (mode) => void openBusinessCaseByNumber(num, mode),
        },
      ];
    },
    [t, openBusinessCaseByNumber]
  );

  // Historie: znovuotevření PŘÍMO (legacy přes GlobalName — menuItemId je už po reloadu neplatné,
  // nejdeme přes Magic round-trip). mode 'replace' = nahradit plochu (klik), 'detached' = samostatné okno (čudlík).
  const historyOpen = useCallback(
    (item: AgendaHistoryItem, mode: 'replace' | 'detached') => {
      if (item.kind === 'native') {
        // `target` je stabilní agendaId → najdi agendu v registru a otevři ji přímo z uložených
        // parametrů (samostatné okno). Move-to-top v historii zajistí useAgendaHistoryRecorder.
        const agenda = findNativeAgendaById(item.target);
        if (!agenda) {
          // Zpětná kompatibilita se staršími záznamy, kde `target` byla cesta menu (ne agendaId).
          if (mode === 'detached') openNativeDetached(item.target);
          else handleMenuNavigate(item.target);
          return;
        }
        const params = item.params ?? {};
        if (mode === 'detached') {
          void kernel.openModal(agenda, params).then(() => {
            const newId = kernel.getState().activeTopLayerId;
            // Fresh stackId → projekce zobrazí okno jako floating (PhoenixDesktop naseeduje rect).
            if (newId) kernel.setLayerStackId(newId, `s_${crypto.randomUUID()}`);
          });
        } else {
          void kernel.openRoot(agenda, params);
        }
        return;
      }
      const id = recordAgendaHistory(item); // move-to-top (legacy)
      const params = { formGlobalName: item.target, formParams: item.params };
      const opened =
        mode === 'detached'
          ? kernel.openModal(legacyFormAgenda, params)
          : kernel.openRoot(legacyFormAgenda, params);
      void opened.then(() => {
        const newId = kernel.getState().activeTopLayerId;
        if (newId) {
          if (mode === 'detached') {
            // Fresh stackId → projekce zobrazí okno jako floating (PhoenixDesktop naseeduje rect).
            kernel.setLayerStackId(newId, `s_${crypto.randomUUID()}`);
          }
          linkLayerToHistory(newId, id);
        }
      });
    },
    [kernel, handleMenuNavigate, openNativeDetached]
  );

  const leadingItems = leadingStandard.map((item) => (
    <button
      key={item.id}
      type="button"
      className="legacy-menu__item"
      data-testid={`menu-item-${item.id}`}
      data-active={getNativeRootAgenda(item.path)?.id === plochaRootAgendaId ? 'true' : undefined}
      onClick={() => handleMenuNavigate(item.path)}
    >
      {item.label}
    </button>
  ));

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Router adapter works against the stable TanStack runtime surface.
    const adapter = createTanStackRouterAdapter(router as any);
    kernel.connectRouter(adapter);
  }, [kernel, router]);

  useEffect(() => {
    registerAgendas(kernel, agendas);
    // Globální registrace VŠECH native agend → libovolnou lze otevřít jako osamostatněné (floating)
    // okno bez návštěvy její route a floating okna přežijí reload/deep-link (restore z URL je dohledá).
    registerAgendas(kernel, getAllNativeAgendaRegistrations());
    // Mapování „ObjectCategory → native agenda" → ⋯/proklik v selektorech, sendMagicFormEdit a
    // legacy `form/open` se pro dané kategorie (např. Station) přesměrují na native agendy.
    installObjectCategoryNav(kernel);
    // Výsledkové akce background jobů (composition wiring — jobs modul o modulech nic neví).
    registerVenuaPlannerJobResultActions();
  }, [kernel, agendas]);

  useEffect(() => {
    // Cold boot / reload / deep-link (prázdný kernel) → obnov stav z URL (vč. floating oken).
    // Agendy se otevírají přímo přes openRoot (handleMenuNavigate), ne přes route navigaci, takže
    // in-app už není co řešit. `rootAgenda` auto-open využívá jen legacy-form route (deep-link);
    // prázdná plocha na '/' (DesktopShellPage) rootAgendu nemá.
    if (kernel.getState().layers.length === 0) {
      kernel.restoreFromCurrentUrl();
      if (rootAgenda && kernel.getState().layers.length === 0) {
        void kernel.openRoot(rootAgenda, {});
      }
    }
  }, [kernel, rootAgenda]);

  // Globální zkratka Alt+Q → focus do našeptávače agend. Detekce přes fyzickou klávesu
  // (`e.code === 'KeyQ'`, nezávislé na layoutu i OS — funguje i jako Option+Q v Safari/macOS),
  // `!ctrlKey` vyloučí AltGr (Win), `!metaKey` vyloučí Cmd. Z legacy oken (cross-origin Magic iframe)
  // přichází focus přes postMessage `desktop/focus-search` (Magic ho pošle z keydown handleru).
  useEffect(() => {
    const focusSearch = () => searchRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.code === 'KeyQ') {
        e.preventDefault();
        focusSearch();
      }
    };
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { action?: string } | null;
      if (data?.action === MAGIC_TO_PHOENIX_ACTIONS.desktopFocusSearch) focusSearch();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true });
      window.removeEventListener('message', onMessage);
    };
  }, []);

  return (
    <NavigationShellFrame
      mainMenu={
        <MainMenu
          sections={sections}
          loading={loading}
          loadError={menuLoadError}
          envTintColor={envTintColor}
          onOpen={(ctx) => handleOpen(ctx, 'replace')}
          onDetach={(ctx) => handleOpen(ctx, 'detached')}
          logo={
            <>
              Magic<span className="legacy-menu__logo-strong">3G</span>
            </>
          }
          leadingItems={leadingItems}
          rightContent={
            <div className="gap-phx-3 flex items-center">
              <MenuSearch
                ref={searchRef}
                sections={sections}
                onOpen={handleOpen}
                specialResults={menuSearchSpecials}
              />
              <LanguageSwitcher />
              <OpsIndicator />
              <SchemeSwitcher />
              <ModeSwitcher />
              <DesktopHeaderControls />
            </div>
          }
        />
      }
      footer={
        <PhoenixTaskbar
          buildLabel={versionLabel}
          historySlot={
            <AgendaHistoryButton
              onOpen={(item) => historyOpen(item, 'replace')}
              onDetach={(item) => historyOpen(item, 'detached')}
            />
          }
          infoSlot={<DevelopmentDatabaseBadge />}
        />
      }
    >
      <PhoenixDesktop />
      <WindowOnboarding />
      <JobsRuntime />
    </NavigationShellFrame>
  );
}
